import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import emailjs from "@emailjs/browser";
import "../styles/Contact.css";

// Constants
const EMAIL = "securedev001@gmail.com";
const SERVICES = [
  "Software & Web Development",
  "Cybersecurity",
  "Tech Repair & Support",
];

const EMAILJS_CONFIG = {
  serviceId: "service_vgjy99e",
  templateId: "template_htbpipb",
  publicKey: "-GiQkeLeazI3t4uSC",
};

const INITIAL_FORM_STATE = {
  name: "",
  email: "",
  service: "Software & Web Development",
  message: "",
};

// Subcomponents
const FormField = ({
  label,
  type = "text",
  id,
  name,
  value,
  onChange,
  placeholder,
  required,
  as = "input",
}) => {
  const Component = as;

  return (
    <div className="form-group">
      <label htmlFor={id}>
        {label}
        {required && <span className="required">*</span>}
      </label>
      <Component
        type={type}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        {...(as === "textarea" && { rows: 4 })}
      />
    </div>
  );
};

const ContactForm = ({
  formData,
  onChange,
  onSubmit,
  loading,
  submitted,
  error,
}) => (
  <form className="contact-form" onSubmit={onSubmit}>
    {submitted && (
      <div className="success-message">
        <FaCheckCircle aria-hidden="true" />
        <div>
          <strong>Thank you!</strong>
          <p>Your message has been received. We'll be in touch soon.</p>
        </div>
      </div>
    )}

    {error && (
      <div className="error-message" role="alert">
        {error}
      </div>
    )}

    <FormField
      label="Full Name"
      id="name"
      name="name"
      value={formData.name}
      onChange={onChange}
      placeholder="Your Name"
      required
    />

    <FormField
      label="Email Address"
      type="email"
      id="email"
      name="email"
      value={formData.email}
      onChange={onChange}
      placeholder="your@email.com"
      required
    />

    <div className="form-group">
      <label htmlFor="service">
        Service Interested In
        <span className="required">*</span>
      </label>
      <select
        id="service"
        name="service"
        value={formData.service}
        onChange={onChange}
        required
      >
        {SERVICES.map((service) => (
          <option key={service} value={service}>
            {service}
          </option>
        ))}
      </select>
    </div>

    <FormField
      label="Message"
      id="message"
      name="message"
      value={formData.message}
      onChange={onChange}
      placeholder="Tell us about your project or inquiry..."
      required
      as="textarea"
    />

    <button type="submit" className="submit-button" disabled={loading}>
      {loading ? "Sending..." : "Send Message"}
    </button>
  </form>
);

// Main Component
/**
 * Contact Section Component
 * Clean contact form that sends directly to email via EmailJS
 * User email is not exposed on the page
 */
function Contact() {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Store in localStorage (optional backup)
      const contactData = {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        type: "contact",
        ...formData,
        status: "pending",
      };

      const contacts = JSON.parse(localStorage.getItem("contacts")) || [];
      contacts.push(contactData);
      localStorage.setItem("contacts", JSON.stringify(contacts));

      // Send email via EmailJS (backend only)
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          to_email: EMAIL,
          from_email: formData.email,
          from_name: formData.name,
          service: formData.service,
          message: formData.message,
        },
        EMAILJS_CONFIG.publicKey,
      );

      // Success state
      setSubmitted(true);
      setFormData(INITIAL_FORM_STATE);

      // Hide success message after 4 seconds
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setError("Error sending message. Please try again or check back later.");
      console.error("Contact form error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="contact-container">
        <div className="section-header">
          <h2>Get In Touch</h2>
          <p>Send us a message and we'll respond as soon as possible</p>
        </div>

        <div className="contact-content">
          <ContactForm
            formData={formData}
            onChange={handleChange}
            onSubmit={handleSubmit}
            loading={loading}
            submitted={submitted}
            error={error}
          />
        </div>
      </div>
    </section>
  );
}

export default Contact;
